/**
 * Canadian Statutory Holidays Utility
 * Calculates federal and provincial statutory holidays for Canada
 */

export interface Holiday {
  date: Date;
  name: string;
  type: 'federal' | 'provincial';
  provinces?: string[]; // If provincial, which provinces observe it
}

/**
 * Calculate Easter Sunday using the Anonymous Gregorian algorithm
 */
function getEasterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  
  return new Date(year, month - 1, day);
}

/**
 * Get Good Friday (Friday before Easter)
 */
function getGoodFriday(year: number): Date {
  const easter = getEasterSunday(year);
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);
  return goodFriday;
}

/**
 * Get Easter Monday (Monday after Easter)
 */
function getEasterMonday(year: number): Date {
  const easter = getEasterSunday(year);
  const easterMonday = new Date(easter);
  easterMonday.setDate(easter.getDate() + 1);
  return easterMonday;
}

/**
 * Get Victoria Day (Monday before May 25)
 */
function getVictoriaDay(year: number): Date {
  const may24 = new Date(year, 4, 24);
  const dayOfWeek = may24.getDay();
  const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const victoriaDay = new Date(may24);
  victoriaDay.setDate(24 + daysToMonday);
  return victoriaDay;
}

/**
 * Get Labour Day (first Monday of September)
 */
function getLabourDay(year: number): Date {
  const sept1 = new Date(year, 8, 1);
  const dayOfWeek = sept1.getDay();
  const daysToMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 0 : 8 - dayOfWeek;
  const labourDay = new Date(sept1);
  labourDay.setDate(1 + daysToMonday);
  return labourDay;
}

/**
 * Get Thanksgiving (second Monday of October)
 */
function getThanksgiving(year: number): Date {
  const oct1 = new Date(year, 9, 1);
  const dayOfWeek = oct1.getDay();
  const daysToFirstMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 0 : 8 - dayOfWeek;
  const thanksgiving = new Date(oct1);
  thanksgiving.setDate(1 + daysToFirstMonday + 7); // Second Monday
  return thanksgiving;
}

/**
 * Get all Canadian statutory holidays for a given year
 */
export function getCanadianHolidays(year: number): Holiday[] {
  const holidays: Holiday[] = [
    {
      date: new Date(year, 0, 1),
      name: "New Year's Day",
      type: 'federal'
    },
    {
      date: getGoodFriday(year),
      name: 'Good Friday',
      type: 'federal'
    },
    {
      date: getEasterMonday(year),
      name: 'Easter Monday',
      type: 'provincial',
      provinces: ['QC'] // Quebec only
    },
    {
      date: getVictoriaDay(year),
      name: 'Victoria Day',
      type: 'federal'
    },
    {
      date: new Date(year, 6, 1),
      name: 'Canada Day',
      type: 'federal'
    },
    {
      date: getLabourDay(year),
      name: 'Labour Day',
      type: 'federal'
    },
    {
      date: getThanksgiving(year),
      name: 'Thanksgiving',
      type: 'federal'
    },
    {
      date: new Date(year, 10, 11),
      name: 'Remembrance Day',
      type: 'provincial',
      provinces: ['AB', 'SK', 'BC', 'PE', 'NL', 'NT', 'NU', 'YT']
    },
    {
      date: new Date(year, 11, 25),
      name: 'Christmas Day',
      type: 'federal'
    },
    {
      date: new Date(year, 11, 26),
      name: 'Boxing Day',
      type: 'provincial',
      provinces: ['ON', 'NB', 'NS']
    }
  ];

  // National Day for Truth and Reconciliation (September 30) - Federal since 2021
  if (year >= 2021) {
    holidays.push({
      date: new Date(year, 8, 30),
      name: 'National Day for Truth and Reconciliation',
      type: 'federal'
    });
  }

  return holidays.sort((a, b) => a.date.getTime() - b.date.getTime());
}

/**
 * Check if a date is a Canadian holiday
 */
export function isHoliday(date: Date, holidays?: Holiday[]): Holiday | null {
  const year = date.getFullYear();
  const holidayList = holidays || getCanadianHolidays(year);
  
  // Compare local calendar dates (toISOString shifts days for non-UTC users).
  const key = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const dateStr = key(date);
  const holiday = holidayList.find(h => key(h.date) === dateStr);

  return holiday || null;
}

/**
 * Get holiday name for a specific date
 */
export function getHolidayName(date: Date): string | null {
  const holiday = isHoliday(date);
  return holiday ? holiday.name : null;
}

/**
 * Check if a date is a weekend (Saturday or Sunday)
 */
export function isWeekend(date: Date): boolean {
  const dayOfWeek = date.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
}

/**
 * Check if a date is a working day (not weekend or holiday)
 */
export function isWorkingDay(date: Date, holidays?: Holiday[]): boolean {
  return !isWeekend(date) && !isHoliday(date, holidays);
}

/**
 * Get the next available working day (skip weekends and holidays)
 * @param date - The starting date
 * @param holidays - Optional list of holidays to check against
 * @returns The next working day
 */
export function getNextWorkingDay(date: Date, holidays?: Holiday[]): Date {
  const year = date.getFullYear();
  const holidayList = holidays || getCanadianHolidays(year);
  
  // Start from the given date
  const nextDay = new Date(date);
  
  // Keep advancing until we find a working day
  while (!isWorkingDay(nextDay, holidayList)) {
    nextDay.setDate(nextDay.getDate() + 1);
    
    // If we've moved to a new year, refresh holidays list
    if (nextDay.getFullYear() !== year) {
      holidayList.push(...getCanadianHolidays(nextDay.getFullYear()));
    }
  }
  
  return nextDay;
}

/**
 * Adjust a date to the next working day if it falls on a weekend or holiday
 * @param date - The date to adjust
 * @param holidays - Optional list of holidays to check against
 * @returns The adjusted date (same date if already a working day, or next working day)
 */
export function adjustToWorkingDay(date: Date, holidays?: Holiday[]): Date {
  if (isWorkingDay(date, holidays)) {
    return date;
  }
  return getNextWorkingDay(date, holidays);
}
